import { belongsToOkrTeam } from './okrTeamScope';
import { getCalculationTypeLabel } from './okrCalculationLabel';
import React, { useCallback, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';

const getId = (value) => value?._id || value?.id || value || '';
const sameId = (a, b) => String(getId(a)) === String(getId(b));

const formatNumber = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number.toLocaleString('es-PA', { maximumFractionDigits: 2 }) : '0';
};

const getUserName = (user) => {
    if (!user) return '-';
    if (typeof user === 'string') return user;
    if (user.name || user.fullName) return user.name || user.fullName;
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name || user.email || '-';
};

const getPointOwnerId = (keyResult) => {
    return getId(keyResult?.ownerId || keyResult?.pointOwnerUserId || keyResult?.updateResponsibleUserId);
};

const getPointOwnerValue = (keyResult, userMap = {}) => {
    const ownerId = getPointOwnerId(keyResult);
    return userMap[ownerId] || keyResult?.ownerId || keyResult?.pointOwnerUserId || keyResult?.updateResponsibleUserId;
};

const getStatusLabel = (status) => {
    const labels = {
        draft: 'Borrador',
        submitted: 'Enviada',
        approved: 'Aprobada',
        rejected: 'Rechazada'
    };
    return labels[status] || 'Pendiente';
};

const getStatusSeverity = (status) => {
    if (status === 'approved') return 'success';
    if (status === 'submitted') return 'info';
    if (status === 'rejected') return 'danger';
    if (status === 'draft') return 'warning';
    return 'secondary';
};

const getAssignmentStatus = (assignedPoints, pointsBase) => {
    if (assignedPoints > pointsBase) return { label: 'Sobreasignado', severity: 'danger' };
    if (assignedPoints === pointsBase) return { label: 'Completo', severity: 'success' };
    return { label: 'Incompleto', severity: 'warning' };
};


const normalizeTitle = (value) =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

const isPrimarySalesKeyResult = (keyResult) => {
    const title = keyResult?.title || '';
    return /\bventas?\b/.test(normalizeTitle(title)) && /\$/.test(title);
};

const putPrimarySalesKeyResultFirst = (keyResults = []) =>
    keyResults
        .map((keyResult, originalIndex) => ({
            keyResult,
            originalIndex,
            salesPriority: isPrimarySalesKeyResult(keyResult) ? 0 : 1
        }))
        .sort((a, b) => a.salesPriority - b.salesPriority || a.originalIndex - b.originalIndex)
        .map(({ keyResult }) => keyResult);

const AUTOMATED_SOURCE_LABELS = {
    quickbooks: 'QuickBooks',
    parlevel: 'Parlevel',
    syncro_operations: 'Syncro',
    composite: 'Compuesta'
};

const AUTOMATED_SOURCE_DESCRIPTIONS = {
    quickbooks: 'Facturación comercial / contable',
    parlevel: 'Máquinas vending / POS / rutas',
    syncro_operations: 'Syncro Operaciones',
    composite: 'Métrica compuesta'
};

const OkrWeeklyBoard = ({
    selectedCycle,
    teams = [],
    objectives = [],
    keyResults = [],
    weeklyEntries = [],
    users = [],
    currentUser,
    canManage,
    scoreboard = [],
    teamFilter = '',
    ownerFilter = '',
    selectedWeekNumber = null,
    hideEmptyPeople = false,
    boardTitle = 'Tablero semanal de puntos',
    boardDescription = 'Ingresa valores reales desde cada celda. Los puntos se calculan automáticamente al guardar; este tablero solo los muestra.',
    emptyTitle = 'No hay resultados clave para mostrar en el tablero.',
    emptyDescription = 'El tablero se llena con resultados clave activos del ciclo seleccionado.',
    onWeekChange,
    onOpenEntry
}) => {
    const weeks = useMemo(() => (Array.isArray(selectedCycle?.weeks) ? selectedCycle.weeks : []), [selectedCycle]);
    const currentUserId = getId(currentUser);
    const pointsBase = Number(selectedCycle?.pointsBase || 1000);
    const numberOfWeeks = Number(selectedCycle?.numberOfWeeks || 0);
    const expectedWeeklyTarget = numberOfWeeks > 0 ? pointsBase / numberOfWeeks : 0;
    const selectedWeek = useMemo(
        () =>
            weeks.find((week) => Number(week.weekNumber) === Number(selectedWeekNumber)) ||
            weeks[0] ||
            null,
        [selectedWeekNumber, weeks]
    );

    const objectiveMap = useMemo(() => objectives.reduce((map, objective) => ({ ...map, [getId(objective)]: objective }), {}), [objectives]);
    const userMap = useMemo(() => users.reduce((map, user) => ({ ...map, [getId(user)]: user }), {}), [users]);
    const scoreboardMap = useMemo(
        () => scoreboard.reduce((map, row) => ({ ...map, [getId(row?.team) || getId(row?.teamId)]: row }), {}),
        [scoreboard]
    );

    const entriesByKeyResultWeek = useMemo(() => {
        return weeklyEntries.reduce((map, entry) => {
            const key = `${getId(entry?.keyResultId)}-${Number(entry?.weekNumber)}`;
            map[key] = entry;
            return map;
        }, {});
    }, [weeklyEntries]);

    const getVisibleKeyResults = useCallback((team) =>
        keyResults.filter((keyResult) => {
            if (keyResult?.isActive === false) return false;
            if (!belongsToOkrTeam(keyResult, team)) return false;
            if (teamFilter && getId(team) !== teamFilter) return false;
            if (ownerFilter && getPointOwnerId(keyResult) !== ownerFilter) return false;
            if (canManage) return true;
            const sourceTeam = team.isManagementGroup ? teams.find(item => getId(item) === getId(keyResult.teamId)) : team;
            if (getId(sourceTeam?.managerId) === currentUserId || (sourceTeam?.readOnlyViewerIds || []).some(id => getId(id) === currentUserId)) return true;
            return getPointOwnerId(keyResult) === currentUserId;
        }), [canManage, currentUserId, keyResults, ownerFilter, teamFilter, teams]);

    const teamsWithRows = useMemo(() => {
        return teams
            .filter((team) => teamFilter ? getId(team) === teamFilter : !team.isManagementGroup)
            .map((team) => {
                const visibleKeyResults = getVisibleKeyResults(team);
                const members = Array.isArray(team.memberIds) ? team.memberIds : [];
                const peopleById = new Map();
                members.forEach((member) => {
                    const memberId = getId(member);
                    if (memberId) peopleById.set(memberId, member);
                });
                visibleKeyResults.forEach((keyResult) => {
                    const ownerId = getPointOwnerId(keyResult);
                    if (!ownerId || peopleById.has(ownerId)) return;
                    peopleById.set(ownerId, getPointOwnerValue(keyResult, userMap));
                });
                const people = [...peopleById.entries()]
                    .filter((member) => {
                        if (!ownerFilter) return true;
                        return sameId(member[0], ownerFilter);
                    })
                    .map(([memberId, member]) => {
                        const personKeyResults = putPrimarySalesKeyResultFirst(
                            visibleKeyResults.filter((keyResult) => getPointOwnerId(keyResult) === memberId)
                        );
                        const assignedPoints = personKeyResults.reduce((total, keyResult) => total + Number(keyResult.weight || 0), 0);
                        const accumulatedPoints = personKeyResults.reduce((total, keyResult) => {
                            return (
                                total +
                                weeks.reduce((weekTotal, week) => {
                                    const entry = entriesByKeyResultWeek[`${getId(keyResult)}-${Number(week.weekNumber)}`];
                                    return weekTotal + Number(entry?.finalPoints || 0);
                                }, 0)
                            );
                        }, 0);
                        const assignment = getAssignmentStatus(assignedPoints, pointsBase);

                        return {
                            member,
                            memberId,
                            keyResults: personKeyResults,
                            assignedPoints,
                            accumulatedPoints,
                            progressPercentage: pointsBase > 0 ? (accumulatedPoints / pointsBase) * 100 : 0,
                            configuredWeeklyPoints: numberOfWeeks > 0 ? assignedPoints / numberOfWeeks : 0,
                            assignment
                        };
                    })
                    .filter((person) => person.keyResults.length || (!hideEmptyPeople && (canManage || getId(team?.managerId) === currentUserId)));

                return { team, people, score: scoreboardMap[getId(team)] };
            })
            .filter((teamGroup) => teamGroup.people.length);
    }, [canManage, currentUserId, entriesByKeyResultWeek, getVisibleKeyResults, hideEmptyPeople, numberOfWeeks, ownerFilter, pointsBase, scoreboardMap, teamFilter, teams, userMap, weeks]);

    const canEditCell = (team, keyResult, week, entry) => {
        if (canManage) return true;
        if (week?.isLocked) return false;
        if (entry?.status === 'approved') return false;
        return getPointOwnerId(keyResult) === currentUserId;
    };

    const openCell = (team, person, keyResult, week) => {
        const entry = entriesByKeyResultWeek[`${getId(keyResult)}-${Number(week.weekNumber)}`];
        if (!canEditCell(team, keyResult, week, entry)) return;
        onWeekChange(week.weekNumber);
        onOpenEntry({
            keyResult,
            team: team.isManagementGroup ? teams.find(item => getId(item) === getId(keyResult.teamId)) : team,
            objective: objectiveMap[getId(keyResult.objectiveId)],
            entry,
            locked: week?.isLocked,
            weekNumber: week.weekNumber
        });
    };

    if (!teamsWithRows.length) {
        return (
            <div className="surface-card border-round p-4 shadow-1 text-center">
                <i className="pi pi-table text-4xl text-primary mb-3" />
                <h3 className="mt-0 mb-2">{emptyTitle}</h3>
                <p className="text-color-secondary m-0">{emptyDescription}</p>
            </div>
        );
    }

    return (
        <div className="flex flex-column gap-3 okr-weekly-board">
            <div className="surface-50 border-round p-3">
                <div className="font-semibold mb-1">{boardTitle}</div>
                <div className="text-sm text-color-secondary">{boardDescription}</div>
            </div>

            {teamsWithRows.map(({ team, people, score }) => (
                <div className="surface-card border-round p-3 shadow-1 okr-weekly-team" key={getId(team)}>
                    <div className="flex flex-column lg:flex-row lg:align-items-start lg:justify-content-between gap-3 mb-3">
                        <div>
                            <div className="flex align-items-center flex-wrap gap-2 mb-2">
                                <h3 className="m-0">Equipo: {team.name}</h3>
                                <Tag value={team.scoringMode === 'sum' ? 'Suma' : 'Promedio'} severity="info" />
                            </div>
                            <div className="text-sm text-color-secondary">Manager OKR: {getUserName(team.managerId)}</div>
                        </div>
                        <div className="flex flex-column align-items-start lg:align-items-end gap-1">
                            <div className="font-semibold">Puntaje normalizado: {formatNumber(score?.normalizedTeamPoints)} / {formatNumber(pointsBase)}</div>
                            <div className="text-sm text-color-secondary">{formatNumber(score?.teamProgressPercentage)}% avance · Puntos brutos: {formatNumber(score?.rawTotalPoints)}</div>
                        </div>
                    </div>

                    <div className="flex flex-column gap-4">
                        {people.map((person) => (
                            <div className="border-1 surface-border border-round p-3 okr-weekly-person" key={`${getId(team)}-${person.memberId}`}>
                                <div className="flex flex-column lg:flex-row lg:align-items-start lg:justify-content-between gap-3 mb-3">
                                    <div>
                                        <div className="flex align-items-center flex-wrap gap-2 mb-2">
                                            <h4 className="m-0">Colaborador: {getUserName(userMap[person.memberId] || person.member)}</h4>
                                            <Tag value={person.assignment.label} severity={person.assignment.severity} />
                                        </div>
                                        <div className="text-sm text-color-secondary">
                                            {formatNumber(person.assignedPoints)} / {formatNumber(pointsBase)} pts asignados · Acumulado: {formatNumber(person.accumulatedPoints)} pts · {formatNumber(person.progressPercentage)}%
                                        </div>
                                        <div className="text-sm text-color-secondary">
                                            Pts/semana configurados: {formatNumber(person.configuredWeeklyPoints)} · Meta semanal completa: {formatNumber(expectedWeeklyTarget)} pts
                                        </div>
                                    </div>
                                </div>

                                <div className="okr-mobile-week-list">
                                    <div className="okr-mobile-week-heading">
                                        <div>
                                            <div className="text-sm text-color-secondary">Semana seleccionada</div>
                                            <div className="font-semibold">{selectedWeek?.label || `Semana ${selectedWeek?.weekNumber || '-'}`}</div>
                                        </div>
                                        {selectedWeek?.isLocked && !canManage ? <Tag value="Bloqueada" severity="danger" /> : <Tag value="Semana abierta" severity="info" />}
                                    </div>

                                    {person.keyResults.map((keyResult) => {
                                        const pointsPerWeek = numberOfWeeks > 0 ? Number(keyResult.weight || 0) / numberOfWeeks : 0;
                                        const totalAccumulated = weeks.reduce((total, week) => {
                                            const existingEntry = entriesByKeyResultWeek[`${getId(keyResult)}-${Number(week.weekNumber)}`];
                                            return total + Number(existingEntry?.finalPoints || 0);
                                        }, 0);
                                        const entry = selectedWeek
                                            ? entriesByKeyResultWeek[`${getId(keyResult)}-${Number(selectedWeek.weekNumber)}`]
                                            : null;
                                        const editable = selectedWeek ? canEditCell(team, keyResult, selectedWeek, entry) : false;
                                        const locked = Boolean(selectedWeek?.isLocked && !canManage);
                                        const sourceLabel = AUTOMATED_SOURCE_LABELS[keyResult?.automationConfig?.sourceType];

                                        return (
                                            <article className="okr-mobile-kr-card" key={getId(keyResult)}>
                                                <div className="flex align-items-start justify-content-between gap-2">
                                                    <div className="font-semibold okr-mobile-kr-title">{keyResult.title}</div>
                                                    <Tag value={locked ? 'Bloqueada' : getStatusLabel(entry?.status)} severity={locked ? 'danger' : getStatusSeverity(entry?.status)} />
                                                </div>
                                                <div className="text-sm text-color-secondary mt-1">{getCalculationTypeLabel(keyResult.calculationType, keyResult)}</div>
                                                {sourceLabel && keyResult?.automationConfig?.isEnabled !== false ? (
                                                    <div className="mt-2">
                                                        <Tag value={sourceLabel} severity="info" />
                                                    </div>
                                                ) : null}

                                                <div className="okr-mobile-metrics">
                                                    <div>
                                                        <span>Peso</span>
                                                        <strong>{formatNumber(keyResult.weight)} pts</strong>
                                                    </div>
                                                    <div>
                                                        <span>Puntos/semana</span>
                                                        <strong>{formatNumber(pointsPerWeek)} pts</strong>
                                                    </div>
                                                    <div>
                                                        <span>Acumulado</span>
                                                        <strong>{formatNumber(totalAccumulated)} pts</strong>
                                                    </div>
                                                </div>

                                                <div className="okr-mobile-week-result">
                                                    <span className="text-sm text-color-secondary">Resultado de esta semana</span>
                                                    <strong>{entry ? `${formatNumber(entry.finalPoints)} pts` : locked ? 'Semana bloqueada' : 'Pendiente'}</strong>
                                                    {entry?.actualValue !== undefined && entry?.actualValue !== null ? (
                                                        <span className="text-sm text-color-secondary">Valor reportado: {formatNumber(entry.actualValue)}{keyResult.unit ? ` ${keyResult.unit}` : ''}</span>
                                                    ) : null}
                                                </div>

                                                {editable ? (
                                                    <Button
                                                        type="button"
                                                        label={entry ? 'Actualizar puntos' : 'Ingresar avance'}
                                                        icon={entry ? 'pi pi-pencil' : 'pi pi-plus'}
                                                        className="w-full"
                                                        onClick={() => openCell(team, person, keyResult, selectedWeek)}
                                                    />
                                                ) : (
                                                    <div className="text-sm text-color-secondary okr-mobile-readonly-note">
                                                        {locked ? 'Esta semana ya no permite cambios.' : entry?.status === 'approved' ? 'Esta actualización ya fue aprobada.' : 'Esta actualización es de solo lectura.'}
                                                    </div>
                                                )}
                                            </article>
                                        );
                                    })}
                                </div>

                                <div className="overflow-x-auto okr-week-table-scroll">
                                    <table className="w-full okr-week-table" style={{ minWidth: `${620 + weeks.length * 128}px` }}>
                                        <thead>
                                            <tr>
                                                <th className="text-left surface-100 p-2 border-1 surface-border okr-week-table__sticky okr-week-table__key-result">Resultado clave</th>
                                                <th className="text-left surface-100 p-2 border-1 surface-border okr-week-table__sticky okr-week-table__weight">Peso</th>
                                                <th className="text-left surface-100 p-2 border-1 surface-border okr-week-table__sticky okr-week-table__points">Puntos/semana</th>
                                                {weeks.map((week) => (
                                                    <th className="text-left surface-100 p-2 border-1 surface-border" style={{ width: '128px' }} key={week.weekNumber}>
                                                        {week.label || `Semana ${week.weekNumber}`}
                                                    </th>
                                                ))}
                                                <th className="text-left surface-100 p-2 border-1 surface-border" style={{ width: '130px' }}>Total acumulado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {person.keyResults.map((keyResult) => {
                                                const pointsPerWeek = numberOfWeeks > 0 ? Number(keyResult.weight || 0) / numberOfWeeks : 0;
                                                const totalAccumulated = weeks.reduce((total, week) => {
                                                    const entry = entriesByKeyResultWeek[`${getId(keyResult)}-${Number(week.weekNumber)}`];
                                                    return total + Number(entry?.finalPoints || 0);
                                                }, 0);

                                                return (
                                                    <tr key={getId(keyResult)}>
                                                        <td className="p-2 border-1 surface-border vertical-align-top okr-week-table__sticky okr-week-table__key-result">
                                                            <div className="font-medium">{keyResult.title}</div>
                                                            <div className="text-xs text-color-secondary mt-1">{getCalculationTypeLabel(keyResult.calculationType, keyResult)}</div>
                                                            {AUTOMATED_SOURCE_LABELS[keyResult?.automationConfig?.sourceType] && keyResult?.automationConfig?.isEnabled !== false ? (
                                                                <div className="mt-1">
                                                                    <Tag value={AUTOMATED_SOURCE_LABELS[keyResult.automationConfig.sourceType]} severity="info" />
                                                                    <div className="text-xs text-color-secondary mt-1">
                                                                        {AUTOMATED_SOURCE_DESCRIPTIONS[keyResult.automationConfig.sourceType]}
                                                                    </div>
                                                                </div>
                                                            ) : null}
                                                            {keyResult.description ? <div className="text-xs text-color-secondary mt-1">{keyResult.description}</div> : null}
                                                        </td>
                                                        <td className="p-2 border-1 surface-border vertical-align-top okr-week-table__sticky okr-week-table__weight">{formatNumber(keyResult.weight)}</td>
                                                        <td className="p-2 border-1 surface-border vertical-align-top okr-week-table__sticky okr-week-table__points">{formatNumber(pointsPerWeek)}</td>
                                                        {weeks.map((week) => {
                                                            const entry = entriesByKeyResultWeek[`${getId(keyResult)}-${Number(week.weekNumber)}`];
                                                            const editable = canEditCell(team, keyResult, week, entry);
                                                            const locked = week?.isLocked && !canManage;
                                                            const isSelectedWeek = Number(selectedWeekNumber) === Number(week.weekNumber);
                                                            const actionStatus = keyResult.calculationType === 'action_completed' && entry
                                                                ? Number(entry.actualValue) === 1
                                                                    ? 'Completada'
                                                                    : 'No completada'
                                                                : '';
                                                            const maxAllowedStatus = keyResult.calculationType === 'max_allowed' && entry
                                                                ? `${formatNumber(entry.actualValue)}${keyResult.unit ? ` ${keyResult.unit}` : ''}`
                                                                : '';

                                                            return (
                                                                <td className="p-2 border-1 surface-border vertical-align-top" key={week.weekNumber}>
                                                                    <button
                                                                        type="button"
                                                                        className={`w-full text-left border-none border-round p-2 ${editable ? 'cursor-pointer surface-hover' : 'surface-100 cursor-default'}`}
                                                                        style={isSelectedWeek ? { boxShadow: 'inset 0 0 0 2px var(--primary-color)' } : undefined}
                                                                        onClick={() => openCell(team, person, keyResult, week)}
                                                                        disabled={!editable}
                                                                    >
                                                                        <div className="font-semibold">
                                                                            {entry ? `${formatNumber(entry.finalPoints)} pts` : locked ? 'Bloqueada' : 'Pendiente'}
                                                                        </div>
                                                                        {actionStatus ? <div className="text-xs text-color-secondary mt-1">{actionStatus}</div> : null}
                                                                        {maxAllowedStatus ? <div className="text-xs text-color-secondary mt-1">{maxAllowedStatus}</div> : null}
                                                                        <div className="mt-2">
                                                                            {locked ? (
                                                                                <Tag value="Bloqueada" severity="danger" />
                                                                            ) : (
                                                                                <Tag value={getStatusLabel(entry?.status)} severity={getStatusSeverity(entry?.status)} />
                                                                            )}
                                                                        </div>
                                                                        {editable ? <div className="text-xs text-primary mt-2">Actualizar</div> : null}
                                                                    </button>
                                                                </td>
                                                            );
                                                        })}
                                                        <td className="p-2 border-1 surface-border vertical-align-top font-semibold">{formatNumber(totalAccumulated)} pts</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default OkrWeeklyBoard;
